import { Container, Divider } from "@material-ui/core";
import { Box, Grid, Typography } from "@mui/material";
import axios from "axios";
import {
  CurrencyDisplay,
  CustomTable,
  FormCard,
  ModalError,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryButton,
  SectionLabel,
  SkeletonComponent,
  StatusChip,
  SwitchInput,
  TableCellMultipleRow,
} from "components";
import { InnoImage } from "inno-ui";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { Fragment, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import ModalConfirmPaymentSalesInvoice from "./Components/ModalConfirmPaymentSalesInvoice";

const DetailSalesInvoice = ({ userLogin }) => {
  const classes = globalStyles();
  const { state } = useLocation();
  const { id } = useParams();
  const isFirstLoad = useRef(true);
  const inputRef = useRef(null);
  const history = useHistory();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const roleFinance = userLogin?.role?.roles_for === "finance";
  const userPublisher = userLogin?.publisher;

  const [modalVisible, setModalVisible] = useState(false);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingModalButton, setLoadingModalButton] = useState(false);
  const [pageDetail, setPageDetail] = useState([]);
  const showApprovalButtons =
    pageDetail?.status === "waiting_for_approval" && roleFinance;
  const [showDetail, setShowDetail] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const usageInvoice = state?.type === "usage";
  const showExchangeRate =
    usageInvoice && pageDetail?.dsp_currency_symbool !== "Rp";

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handleConfirm = status =>
    ModalWarning(
      `Are you sure to ${status ? "reject" : "approve"} this data ?`,
      `Confirm ${status ? "Reject" : "Approve"} Sales Invoice`
    ).then(res => res?.isConfirmed && handleApproval(status));

  const handlePrintPDF = async () => {
    try {
      setLoadingPrint(true);
      await new Promise(resolve => setTimeout(resolve, 0));

      const doc = new jsPDF("p", "mm", "a4");

      const defaultAddress =
        "Jl. Cideng Barat No.54, RT.12/RW.1, Cideng, Kecamatan Gambir, Kota Jakarta Pusat,\nDaerah Khusus Ibukota Jakarta 10000";

      const fontSmall = 7;
      const fontNormal = 9;
      const ptToMm = pt => pt * 0.352777778;
      const lineHeightFactor = 1.15;

      doc.setFontSize(fontSmall);
      const addressText = userPublisher?.address || defaultAddress;
      const wrappedAddress = doc.splitTextToSize(addressText, 100);
      const addressLineHeight = ptToMm(fontSmall) * lineHeightFactor;
      const lastYAddress = 8 + wrappedAddress.length * addressLineHeight;

      doc.setFontSize(fontNormal);

      const jobNoEndY = 94 + 6;
      const addressEndY = lastYAddress + 10;
      const minStart = 80;

      const headerBottom = Math.ceil(
        Math.max(jobNoEndY, addressEndY, minStart)
      );

      const drawHeader = (doc, pageNumber) => {
        if (userPublisher?.profile_image) {
          try {
            doc.addImage(userPublisher.profile_image, "PNG", 8, 4, 25, 15);
          } catch (err) {
            console.warn("addImage gagal:", err);
          }
        }

        doc.setFontSize(fontSmall);
        doc.setTextColor("#6F6F84");
        doc.text(wrappedAddress, 110, 8);

        const lastY = 8 + wrappedAddress.length * addressLineHeight;
        doc.setDrawColor(200);
        doc.line(8, lastY + 10, 200, lastY + 10);

        doc.setTextColor("#263238");
        doc.setFontSize(fontNormal);

        doc.setFont("helvetica", "normal");
        doc.text("Billed To:", 8, 40);
        doc.setFont("helvetica", "bold");
        doc.text(pageDetail?.dsp_name || "-", 8, 46);

        doc.setFont("helvetica", "normal");
        doc.text("Address:", 8, 52);
        doc.setFont("helvetica", "bold");
        const wrappedComposerAddress = doc.splitTextToSize(
          pageDetail?.dsp_address || "-",
          100
        );
        doc.text(wrappedComposerAddress, 8, 58);

        doc.setFont("helvetica", "normal");
        doc.text("Invoice Number:", 130, 40);
        doc.setFont("helvetica", "bold");
        doc.text(pageDetail?.invoice_number || "-", 130, 46);

        doc.setFont("helvetica", "normal");
        doc.text("Invoice Date:", 130, 52);
        doc.setFont("helvetica", "bold");
        doc.text(pageDetail?.invoice_date || "-", 130, 58);

        doc.setFont("helvetica", "normal");
        doc.text("Product Type:", 130, 64);
        doc.setFont("helvetica", "bold");
        doc.text(pageDetail?.product_type || "-", 130, 70);

        doc.setFont("helvetica", "normal");
        doc.text("Period:", 130, 76);
        doc.setFont("helvetica", "bold");
        doc.text(pageDetail?.period || "-", 130, 82);

        doc.setFont("helvetica", "normal");
        doc.text("Job No:", 130, 88);
        doc.setFont("helvetica", "bold");
        doc.text(pageDetail?.job_number || "-", 130, 94);

        if (pageNumber) {
          doc.setFontSize(8);
          doc.text(
            `Page ${pageNumber}`,
            doc.internal.pageSize.getWidth() - 20,
            doc.internal.pageSize.getHeight() - 8
          );
        }
      };

      let finalY = headerBottom;
      if (showDetail && pageDetail?.songs?.data?.length > 0) {
        const data = pageDetail?.songs?.data?.map(song => {
          return columnTableDetail(pageDetail).map(col => {
            switch (col.name) {
              case "composers":
                if (col.title === "Composer/Author") {
                  return song.composers?.map(c => c.name).join(", ") || "-";
                } else if (col.title === "Ownership Percentage") {
                  return (
                    song.composers
                      ?.map(c => `${c.ownership_percentage || 0}%`)
                      .join(", ") || "-"
                  );
                }
                break;
              case "all":
                return `${pageDetail?.dsp_currency_symbool || "Rp"}${Number(
                  song.revenue || 0
                ).toLocaleString("id-ID", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`;

              default:
                return song[col.name] || "-";
            }
          });
        });

        autoTable(doc, {
          head: [columnTableDetail(pageDetail)?.map(col => col.title)],
          body: data,
          startY: headerBottom,
          margin: { top: headerBottom },
          styles: { fontSize: 8, cellPadding: 3, valign: "middle" },
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: 20,
            fontStyle: "bold",
          },
          didDrawPage: dataArg => {
            drawHeader(doc, dataArg.pageNumber);
          },
        });

        finalY = doc.lastAutoTable.finalY || headerBottom;
        finalY += 10;
      } else {
        drawHeader(doc, 1);
        finalY = headerBottom + 10;
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Fee", 8, finalY);
      doc.text(
        `Rp${Number(pageDetail?.fee || 0).toLocaleString("id-ID")}`,
        200,
        finalY,
        {
          align: "right",
        }
      );

      if (pageDetail?.taxes?.length > 0) {
        pageDetail?.taxes?.forEach(tax => {
          finalY += 8;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          doc.text(tax?.code || "-", 8, finalY);

          const formattedValue = Number(
            Math.abs(tax?.value || 0)
          ).toLocaleString("id-ID", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          const taxText = `${
            tax?.method === "deduction" ? "-" : ""
          }Rp${formattedValue}`;
          doc.text(taxText, 200, finalY, { align: "right" });
        });
      }

      doc.setDrawColor(200);
      doc.line(8, finalY + 4, 200, finalY + 4);

      doc.setFont("helvetica", "bold");
      doc.text("Total Fee", 8, finalY + 12);
      doc.text(
        `Rp${Number(pageDetail?.total_fee || 0).toLocaleString("id-ID")}`,
        200,
        finalY + 12,
        { align: "right" }
      );

      doc.save(`${pageDetail?.invoice_number}.pdf`);
    } finally {
      setLoadingPrint(false);
    }
  };

  const handleApproval = async status => {
    const payload = { status: status } || {};
    try {
      setLoadingButton(true);
      const url = `${hardBaseUrl}/publisher/transaction/v2/sales-invoice/status/${id}`;
      await axios.put(url, payload, { headers });
      ModalSuccess(
        `Successfully ${status ? "Rejected" : "Approved"}`
      ).then(() => history.goBack());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
    }
  };
  const getDataDetail = async () => {
    try {
      setLoadingTable(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/sales-invoice/${id}`,
        { headers, ...(usageInvoice && { params: queryParams }) }
      );
      const data = res?.data?.data?.data;
      setPageDetail(data || {});
      setDataTable(data?.songs?.data || []);
      const pageCount = getTotalPage(data?.songs?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingTable(false);
    }
  };
  const handleConfirmPayment = async () => {
    try {
      setLoadingModalButton(true);
      await axios
        .put(
          `${hardBaseUrl}/publisher/transaction/v2/sales-invoice/status/${id}`,
          {},
          { headers }
        )
        .then(() => {
          ModalSuccess(
            "Your Confirm Payment has been success.",
            "Confirm Payment"
          ).then(() => history.goBack());
        });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingModalButton(false);
      setModalVisible(false);
    }
  };
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getDataDetail()];

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (!isFirstLoad.current) {
      getDataDetail();
    }
  }, [queryParams]);

  return (
    <Page className={classes.root} title="Sales Invoice Detail">
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title="Sales Invoice Detail"
            breadcrumbData={breadcrumbData}
            backButton
          />
          <Divider className={classes.divider} />
          <Grid container>
            <Grid item xs={12}>
              <Grid container justifyContent="space-between">
                <Grid item xs={6}>
                  <SectionLabel
                    title="Sales Invoice Detail"
                    subTitle="This is detail credit note information, in advance transaction"
                  />
                </Grid>
                <Grid
                  item
                  display="flex"
                  justifyContent="right"
                  alignItems="center"
                  xs={6}
                >
                  <StatusChip
                    label={
                      optionsInvoiceStatus?.find(
                        value => value?.value === pageDetail?.status
                      )?.name || "-"
                    }
                    type={
                      optionsInvoiceStatus?.find(
                        value => value?.value === pageDetail?.status
                      )?.color || ""
                    }
                  />
                </Grid>
              </Grid>
              <FormCard ref={inputRef}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <InnoImage
                      src={
                        userLogin?.publisher.profile_image ||
                        "https://sandbox.minio.asaba.co.id/staging-mpis/publisher/ebf4813a-c07f-4ef2-a055-4d9b8597a870.png"
                      }
                      alt="img"
                      fill={true}
                      styleImage={{
                        maxWidth: "155.52px",
                        width: "100%",
                        height: "100%",
                        maxHeight: "50px",
                        objectFit: "contain",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography textAlign="right" fontSize={14} color="#6F6F84">
                      Jl. Setia Budi, RT 05 RW 05, Karet Kuningan, South Jakarta
                    </Typography>
                  </Grid>
                </Grid>
                <Divider className={classes.divider} />
                <Grid container spacing={2} mb={2}>
                  {formInvoiceDetail({ showExchangeRate })?.map(
                    ({ label, value, blank }, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        {!blank && (
                          <Fragment>
                            <Typography>{`${label}:`}</Typography>

                            <SectionLabel
                              title={
                                value === "exchange_rate" ? (
                                  <CurrencyDisplay
                                    value={pageDetail?.[value] || 0}
                                    prefix="Rp"
                                    textAlign="left"
                                  />
                                ) : (
                                  pageDetail?.[value] || "-"
                                )
                              }
                            />
                          </Fragment>
                        )}
                      </Grid>
                    )
                  )}
                  {usageInvoice && (
                    <Grid item xs={12}>
                      <SwitchInput
                        checked={showDetail}
                        onChange={() => setShowDetail(show => !show)}
                        label="Show Song Detail"
                      />
                    </Grid>
                  )}
                </Grid>
                {showDetail && (
                  <CustomTable
                    isLoading={loadingTable}
                    data={dataTable}
                    columnTable={columnTableDetail(pageDetail)}
                    page={queryParams?.page}
                    pageSize={queryParams?.size}
                    pageCount={tableTotalPage}
                    handleChangePage={(_, page) =>
                      handleChangeQueryParams(page, "page")
                    }
                    handleChangePageSize={e =>
                      handleChangeQueryParams(e?.target?.value, "size")
                    }
                    my="16px"
                  />
                )}
                <Box borderRadius="5px" p="24px" bgcolor="#F9FAFB">
                  {formInvoiceValue?.map(
                    ({ label, value, type, divider }, index) => {
                      const isTotal = value === "total_fee";
                      return divider ? (
                        <Divider className={classes.divider} key={index} />
                      ) : type === "taxes" ? (
                        pageDetail?.taxes?.map((item, idx) => (
                          <Grid
                            container
                            justifyContent="space-between"
                            mb="16px"
                            key={idx}
                          >
                            <Grid item>
                              <Typography>{`${item?.code}`}</Typography>
                            </Grid>
                            <Grid item>
                              <Typography>
                                <CurrencyDisplay
                                  value={item?.value}
                                  prefix="Rp"
                                  decimalScale={2}
                                />
                              </Typography>
                            </Grid>
                          </Grid>
                        ))
                      ) : (
                        <Grid
                          container
                          justifyContent="space-between"
                          mb={!isTotal && "16px"}
                          key={index}
                        >
                          <Grid item>
                            <Typography>{label}</Typography>
                          </Grid>
                          <Grid item>
                            <Typography fontWeight={isTotal && 500}>
                              <CurrencyDisplay
                                value={pageDetail?.[value]}
                                prefix="Rp"
                                decimalScale={2}
                              />
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    }
                  )}
                </Box>
              </FormCard>
              <Grid container justifyContent="right" gap={1}>
                <PrimaryButton
                  onClick={handlePrintPDF}
                  disabled={loadingButton || loadingPrint || loadingModalButton}
                  loading={loadingPrint || loadingModalButton || loadingButton}
                  label={loadingPrint ? "Generating PDF..." : "Print"}
                />
                {showApprovalButtons &&
                  approvalButtons?.map(({ label, value }, index) => (
                    <PrimaryButton
                      key={index}
                      label={
                        loadingButton || loadingModalButton || loadingPrint
                          ? "Processing..."
                          : label
                      }
                      disabled={
                        loadingButton || loadingPrint || loadingModalButton
                      }
                      loading={
                        loadingButton || loadingPrint || loadingModalButton
                      }
                      onClick={() => handleConfirm(value)}
                      color={value === "rejected" && "#FF3E00"}
                    />
                  ))}
                {pageDetail?.status === "approved" && roleFinance && (
                  <PrimaryButton
                    onClick={() => setModalVisible(true)}
                    label="Confirm Payment"
                    loading={
                      loadingButton || loadingPrint || loadingModalButton
                    }
                    disabled={
                      loadingButton || loadingPrint || loadingModalButton
                    }
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      )}
      {modalVisible && (
        <ModalConfirmPaymentSalesInvoice
          open={modalVisible}
          onClose={() => setModalVisible(false)}
          loadingButton={loadingModalButton}
          pageDetail={pageDetail}
          handleSubmit={handleConfirmPayment}
        />
      )}
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/sales-invoice",
  },
  {
    label: "Detail Transaction",
    active: true,
  },
];
export const optionsInvoiceStatus = [
  {
    name: "Draft",
    value: "draft",
    color: "warning",
  },
  {
    name: "Waiting for Approval",
    value: "waiting_for_approval",
    color: "warning",
  },
  {
    name: "Approved",
    value: "approved",
    color: "success",
  },
  {
    name: "Paid Updating Dashboard",
    value: "paid_updating_dashboard",
    color: "warning",
  },
  {
    name: "Paid",
    value: "paid",
    color: "success",
  },
  {
    name: "Rejected",
    value: "rejected",
    color: "danger",
  },
];
const columnTableDetail = pageDetail => [
  {
    name: "song_title",
    title: "Song",
  },
  {
    name: "composers",
    title: "Composer/Author",
    renderText: composers => (
      <TableCellMultipleRow type="chip" list={composers} itemKey="name" />
    ),
  },
  {
    name: "composers",
    title: "Ownership Percentage",
    renderText: item => (
      <TableCellMultipleRow
        type="chip"
        list={item}
        itemKey="ownership_percentage"
        suffix="%"
      />
    ),
  },
  {
    name: "right_type",
    title: "Type",
  },
  {
    name: "notes",
    title: "Notes",
  },
  {
    name: "all",
    title: "Royalty",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay
        value={item?.revenue}
        suffix=",-"
        prefix={pageDetail?.dsp_currency_symbool || "Rp"}
        decimalScale={2}
      />
    ),
  },
];
const formInvoiceDetail = ({ showExchangeRate }) => [
  { label: "Billed To", value: "dsp_name" },
  { label: "Invoice Number", value: "invoice_number" },
  { label: "Address", value: "dsp_address" },
  { label: "Invoice Date", value: "invoice_date" },
  { blank: true },
  { label: "Product Type", value: "product_type" },
  { blank: true },
  { label: "Period", value: "period" },
  { blank: true },
  { label: "Job no", value: "job_number" },
  ...(showExchangeRate
    ? [{ blank: true }, { label: "Exchange Rate", value: "exchange_rate" }]
    : []),
];
const formInvoiceValue = [
  { label: "Fee", value: "fee" },
  { type: "taxes" },
  { divider: true },
  { label: "Total Fee", value: "total_fee" },
];
const approvalButtons = [
  { label: "Reject", value: "rejected" },
  { label: "Approve" },
];

export default DetailSalesInvoice;
